import styled from 'styled-components';

import { MEDIA_QUERIES } from '#/hooks/useResponsiveValue';
import { MarginlessMarkdown } from '#/pages/Settings/components/styled';
import { PlanPickerSubText } from '#/plugins/paywall/components';
import { BreakpointKeys } from '#/service/constants';
import { remify } from '#/service/utils';

import { lineClamp } from '../LineClampText';

export const PROMOTIONAL_LABEL_HEIGHT_PX = 40;

export const CardWrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  position: relative;
  min-width: 0;
`;

export const PromotionalLabel = styled.div`
  position: absolute;
  top: -${PROMOTIONAL_LABEL_HEIGHT_PX}px;
  left: 0;
  right: 0;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background-image: linear-gradient(
    to bottom,
    var(--iapGradientTop),
    var(--iapGradientBottom)
  );
  color: var(--iapForeground);
  text-align: center;
  padding: ${remify(8)} ${remify(16)};
  border-radius: ${remify(8)} ${remify(8)} 0 0;
  font: var(--fontStyleTitle);
  box-sizing: border-box;
  height: ${PROMOTIONAL_LABEL_HEIGHT_PX}px;
  ${lineClamp(1)};
  overflow-x: hidden;
`;

export const Card = styled.div<{ $hasPromotionalLabel?: boolean }>`
  display: flex;
  padding: ${remify(24)};
  border-radius: ${({ $hasPromotionalLabel }) =>
    $hasPromotionalLabel ? `0 0 ${remify(8)} ${remify(8)}` : remify(8)};
  background: var(--pageSecondaryBackground);
  color: var(--iapForeground);
  min-height: ${remify(300)};
  box-sizing: border-box;
  width: 100%;
  flex: 1;
  text-align: left;
  min-width: 0;

  @media ${MEDIA_QUERIES[BreakpointKeys.SM]} {
    min-height: auto;
    height: auto;
  }
`;

export const CardContent = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  min-width: 0;
`;

export const DescriptionMarkdown = styled(MarginlessMarkdown)<{
  $isExpanded: boolean;
}>`
  display: flex;
  flex-direction: column;
  gap: ${remify(8)};
  min-width: 0;
  overflow: hidden;

  p {
    ${lineClamp(2)};
  }

  @media ${MEDIA_QUERIES[BreakpointKeys.SM]} {
    p {
      display: -webkit-box;
      -webkit-line-clamp: unset;
      -webkit-box-orient: vertical;
      white-space: normal;
      overflow: visible;
      text-overflow: unset;

      &:nth-child(n + 4) {
        display: ${({ $isExpanded }) => ($isExpanded ? 'block' : 'none')};
      }
    }
  }
`;

export const Title = styled.h3`
  font: var(--fontStyleTitle3);
  margin: 0;
  color: var(--iapForeground);
  order: 1;
  ${lineClamp(2)};
`;

export const DetailContainer = styled.div`
  display: flex;
  align-items: baseline;
  gap: ${remify(4)};
  order: 2;
  margin-top: ${remify(8)};
`;

export const DetailText = styled.div`
  font: var(--fontStyleTitle2);
  color: var(--iapForeground);
`;

export const DetailSubtext = styled.div`
  font: var(--fontStyleBody);
  color: var(--iapForeground);
`;

export const Divider = styled.div`
  width: 100%;
  height: 1px;
  background-color: var(--pageParagraphForeground);
  opacity: 0.3;
  order: 3;
  margin-top: ${remify(16)};

  @media ${MEDIA_QUERIES[BreakpointKeys.SM]} {
    display: none;
  }
`;

export const SubtitleContainer = styled(PlanPickerSubText)<{
  $isExpanded: boolean;
  $showExpandButton: boolean;
}>`
  color: var(--pageParagraphForeground);
  order: 4;
  font: var(--fontStyleSubtitle);
  margin-top: ${remify(16)};
  margin-bottom: ${remify(24)};

  @media ${MEDIA_QUERIES[BreakpointKeys.SM]} {
    order: 5;
    margin-bottom: ${({ $showExpandButton }) =>
      $showExpandButton ? remify(24) : 0};
    ${({ $isExpanded }) => !$isExpanded && lineClamp(3)};
  }
`;

export const ActionButton = styled.div`
  order: 5;
  margin-top: auto;
  width: 70%;
  align-self: center;

  @media ${MEDIA_QUERIES[BreakpointKeys.SM]} {
    order: 4;
    margin-top: ${remify(16)};
    width: 100%;
  }
`;

export const ShowMoreButton = styled.button`
  display: none;

  @media ${MEDIA_QUERIES[BreakpointKeys.SM]} {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: ${remify(8)};
    order: 6;
    background: none;
    border: none;
    color: var(--iapForeground);
    font: var(--fontStyleSubtitle);
    padding: ${remify(8)} 0;
    cursor: pointer;
    text-align: center;

    div {
      height: ${remify(16)};
      width: ${remify(16)};
    }
  }
`;
